// Utility formatters for Journey/Playback page
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import i18n from '~/utils/i18n';

dayjs.extend(customParseFormat);

const TIMESTAMP_FORMAT = 'YYYY-MM-DD HH:mm:ss';

function tJourney(key: string, options?: Record<string, unknown>): string {
  return i18n.t(`admin/journey:${key}`, options) as string;
}

function asNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/** "2026-02-03 03:25:39" → "03:25" */
export function formatTimeHM(timestamp: unknown): string {
  const raw = asNonEmptyString(timestamp);
  if (!raw) return '--';

  const parsed = dayjs(raw, TIMESTAMP_FORMAT, true);
  return parsed.isValid() ? parsed.format('HH:mm') : '--';
}

/** "2026-02-03 03:25:39" → "03/02/2026" */
export function formatDate(timestamp: unknown, format = 'DD/MM/YYYY'): string {
  const raw = asNonEmptyString(timestamp);
  if (!raw) return '--';

  const parsed = dayjs(raw, TIMESTAMP_FORMAT, true);
  return parsed.isValid() ? parsed.format(format) : '--';
}

/** meters (or km as float) → "0.04 km" / "5.00 km" */
export function formatDistance(meters: unknown): string {
  const value = Number(meters);
  if (!Number.isFinite(value)) return '--';

  // Values coming from API are already in km (float), treat as km directly
  return `${value.toFixed(2)} km`;
}

/**
 * "00:23:07" → "23 phút"
 * "02:15:30" → "2 giờ 15 phút"
 */
export function formatDuration(durationStr: string): string {
  if (typeof durationStr !== 'string') return '--';

  const raw = durationStr.trim();
  if (!raw) return '--';

  const parts = raw.split(':');
  if (parts.length === 3) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = Math.floor(Number(parts[2])) || 0;

    if (hours > 0 && minutes > 0) {
      return `${hours}${tJourney('format.short.hour')} ${minutes}${tJourney('format.short.minute')}`;
    }
    if (hours > 0) return `${hours}${tJourney('format.short.hour')}`;
    if (minutes > 0) return `${minutes}${tJourney('format.short.minute')}`;
    return `${seconds}${tJourney('format.short.second')}`;
  }

  const numericSeconds = Number(raw);
  if (!Number.isFinite(numericSeconds)) return '--';
  return formatDuration(secondsToDurationStr(Math.max(0, Math.floor(numericSeconds))));
}

/**
 * "HH:MM:SS" → total seconds
 * "02:15:30" → 8130
 */
export function durationStrToSeconds(durationStr: string): number {
  if (typeof durationStr !== 'string') return 0;

  const parts = durationStr.split(':');
  if (parts.length !== 3) return 0;
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const s = Math.floor(Number(parts[2])) || 0;
  return h * 3600 + m * 60 + s;
}

/** total seconds → "HH:MM:SS" string suitable for formatDuration() */
export function secondsToDurationStr(total: number): string {
  const safeTotal = Number.isFinite(total) ? Math.max(0, Math.floor(total)) : 0;
  const h = Math.floor(safeTotal / 3600);
  const m = Math.floor((safeTotal % 3600) / 60);
  const s = safeTotal % 60;
  return [String(h).padStart(2, '0'), String(m).padStart(2, '0'), String(s).padStart(2, '0')].join(':');
}

/**
 * ("2026-02-03 03:25:39", "2026-02-03 03:48:46") → 1387 (seconds)
 */
export function calculateDurationSeconds(startTime: string, endTime: string): number {
  const startRaw = asNonEmptyString(startTime);
  const endRaw = asNonEmptyString(endTime);
  if (!startRaw || !endRaw) return 0;

  const start = dayjs(startRaw, TIMESTAMP_FORMAT, true);
  const end = dayjs(endRaw, TIMESTAMP_FORMAT, true);
  if (!start.isValid() || !end.isValid()) return 0;

  return Math.max(0, end.diff(start, 'second'));
}

/**
 * 3665 -> "1 giờ 1 phút 5 giây"
 * 45 -> "45 giây"
 */
export function formatDurationDetailed(totalSeconds: number): string {
  const safeTotal = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;

  const hours = Math.floor(safeTotal / 3600);
  const minutes = Math.floor((safeTotal % 3600) / 60);
  const seconds = safeTotal % 60;

  if (safeTotal < 60) {
    return tJourney('format.durationDetailed.second', { count: seconds });
  }

  const parts: string[] = [];
  if (hours > 0) parts.push(tJourney('format.durationDetailed.hour', { count: hours }));
  if (minutes > 0) parts.push(tJourney('format.durationDetailed.minute', { count: minutes }));
  if (seconds > 0) parts.push(tJourney('format.durationDetailed.second', { count: seconds }));

  return parts.length > 0 ? parts.join(' ') : tJourney('format.durationDetailed.zeroSeconds');
}

/** 93.0 → "93%" */
export function formatBattery(percent: unknown): string {
  const value = Number(percent);
  if (!Number.isFinite(value)) return '--';
  return `${Math.round(value)}%`;
}

const STATUS_LABELS: Record<number, string> = {
  0: 'route',
  1: 'stop',
  2: 'lost_gprs',
  3: 'lost_gps',
};

export function formatStatusLabel(status: number): string {
  const numericStatus = Number(status);
  const statusKey = STATUS_LABELS[numericStatus];
  return statusKey ? tJourney(`timeline.types.${statusKey}`) : tJourney('timeline.types.unknown');
}
